// src/pages/driverReports/InstallmentCompletionAlerts.js
import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../../firebase/firebase";
import { applyAssignmentCostPlanForMonth } from "./assignmentCostPlanHistory";

const RECIPIENTS = ["abdulla@cll.ae", "tunzeel@cll.ae", "saleh@cll.ae"];

function toDate(value) {
  try {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    if (value instanceof Date) return value;
    if (typeof value?._seconds === "number") return new Date(value._seconds * 1000);
    if (typeof value?.seconds === "number") return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function dateText(value) {
  const d = toDate(value);
  if (!d) return "-";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

function currentMonthText() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthStart(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m - 1, 1, 0, 0, 0, 0);
}

function monthEnd(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return new Date(y, m, 0, 23, 59, 59, 999);
}

function monthIndex(month) {
  const [y, m] = String(month || "").split("-").map(Number);
  if (!y || !m) return null;
  return y * 12 + (m - 1);
}

function monthDiff(fromMonth, toMonth) {
  const from = monthIndex(fromMonth);
  const to = monthIndex(toMonth);
  if (from === null || to === null) return 0;
  return to - from;
}

function dateToMonth(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function roleOf(a) {
  const raw = String(a?.driverRole || a?.assignmentRole || a?.role || "").toLowerCase();
  if (raw.includes("second")) return "second";
  if (raw.includes("main")) return "main";
  return "";
}

function isConfiguredMain(a) {
  const configured = a?.costConfigured === true || a?.installmentMode === "auto";
  return configured && roleOf(a) === "main";
}

function hasInstallmentPlan(a) {
  return (
    a?.installmentPlanEnabled === true ||
    Number(a?.installmentTotalCount || 0) > 0 ||
    Number(a?.installmentCurrentNo || 0) > 0
  );
}

function overlapsMonth(a, month) {
  const start = toDate(a?.startDate);
  const end = toDate(a?.endDate);
  const mStart = monthStart(month);
  const mEnd = monthEnd(month);
  if (!start || !mStart || !mEnd) return false;
  if (start > mEnd) return false;
  if (end && end < mStart) return false;
  return true;
}

function installmentInfoForMonth(a, month) {
  const totalCount = Number(a?.installmentTotalCount || 0);
  const baseNo = Number(a?.installmentCurrentNo || 0);
  const baseMonth =
    a?.installmentBaseMonth || dateToMonth(a?.installmentEffectiveFrom || a?.startDate) || month;

  if (!totalCount || !baseNo || !baseMonth) {
    return {
      currentNo: 0,
      totalCount,
      baseMonth,
      label: "",
      isCompleting: false,
      problem: "Installment plan number/base month is incomplete.",
    };
  }

  const currentNo = baseNo + monthDiff(baseMonth, month);
  const label = `${currentNo}/${totalCount}`;

  return {
    currentNo,
    totalCount,
    baseMonth,
    label,
    isCompleting: currentNo >= totalCount,
    problem: currentNo < 1 ? "Calculated installment number is below 1. Check base month/current no." : "",
  };
}

function money(value) {
  return Number(value || 0).toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function logDocId(month, assignmentId) {
  return `${String(month || "").replace(/[^0-9A-Za-z_-]/g, "_")}_${String(
    assignmentId || ""
  ).replace(/[^0-9A-Za-z_-]/g, "_")}`;
}

export default function InstallmentCompletionAlerts() {
  const [month, setMonth] = useState(currentMonthText());
  const [assignments, setAssignments] = useState([]);
  const [logsByAssignment, setLogsByAssignment] = useState({});
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");

  const loadData = async () => {
    setLoading(true);
    setMessage("");
    try {
      const assignmentSnap = await getDocs(
        query(collection(db, "driverCarAssignments"), orderBy("startDate", "desc"))
      );
      const list = assignmentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAssignments(list);

      const logsSnap = await getDocs(
        query(collection(db, "installmentCompletionEmailLogs"), where("month", "==", month))
      );
      const map = {};
      logsSnap.docs.forEach((d) => {
        const data = d.data() || {};
        if (data.assignmentId) map[String(data.assignmentId)] = { id: d.id, ...data };
      });
      setLogsByAssignment(map);
    } catch (e) {
      console.error("Failed to load installment completion alerts:", e);
      setMessage(e?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const rows = useMemo(() => {
    return (assignments || [])
      .filter((a) => overlapsMonth(a, month))
      .map((a) => applyAssignmentCostPlanForMonth(a, month))
      .filter((a) => isConfiguredMain(a))
      .filter((a) => hasInstallmentPlan(a))
      .map((a) => {
        const info = installmentInfoForMonth(a, month);
        const log = logsByAssignment[String(a.id)] || null;
        return {
          assignmentId: a.id,
          company: a.company || "",
          vehicleId: a.vehicleId || "",
          vehicleNumber: a.vehicleNumber || "",
          vehicleType: a.vehicleType || "",
          driverId: a.driverId || "",
          driverName: a.driverName || "",
          startDate: dateText(a.startDate),
          endDate: a.endDate ? dateText(a.endDate) : "Present",
          monthlyInstallment: Number(a.monthlyInstallment || 0),
          installmentBaseMonth: info.baseMonth,
          installmentNo: info.currentNo,
          installmentTotal: info.totalCount,
          installmentLabel: info.label,
          isCompleting: info.isCompleting,
          problem: info.problem,
          emailSent: Boolean(log?.emailSentAt || log?.sentAt),
          emailSentAt: log?.emailSentAt || log?.sentAt || null,
          logId: log?.id || logDocId(month, a.id),
        };
      })
      .sort((a, b) => {
        if (a.emailSent !== b.emailSent) return a.emailSent ? 1 : -1;
        return `${a.vehicleNumber} ${a.driverName}`.localeCompare(
          `${b.vehicleNumber} ${b.driverName}`,
          undefined,
          { numeric: true, sensitivity: "base" }
        );
      });
  }, [assignments, month, logsByAssignment]);

  const completingRows = rows.filter((r) => r.isCompleting);
  const unsentRows = completingRows.filter((r) => !r.emailSent);

  const sendEmail = async () => {
    if (!unsentRows.length || sending) return;

    const ok = window.confirm(
      `Send installment completion email for ${unsentRows.length} driver(s) for ${month}?\n\nRecipients:\n${RECIPIENTS.join(
        "\n"
      )}`
    );
    if (!ok) return;

    setSending(true);
    setMessage("");
    try {
      const fn = httpsCallable(getFunctions(), "sendInstallmentCompletionEmail");
      const triggeredByEmail = localStorage.getItem("email") || "";
      const payloadItems = unsentRows.map((r) => ({
        assignmentId: r.assignmentId,
        company: r.company,
        vehicleNumber: r.vehicleNumber,
        vehicleType: r.vehicleType,
        driverId: r.driverId,
        driverName: r.driverName,
        startDate: r.startDate,
        endDate: r.endDate,
        monthlyInstallment: r.monthlyInstallment,
        installmentBaseMonth: r.installmentBaseMonth,
        installmentNo: r.installmentNo,
        installmentTotal: r.installmentTotal,
        installmentLabel: r.installmentLabel,
      }));

      const result = await fn({ month, triggeredByEmail, items: payloadItems });
      const data = result?.data || {};
      setMessage(
        `Email sent/processed. Sent: ${data.sentCount || 0}, skipped duplicate: ${data.skippedCount || 0}.`
      );
      await loadData();
    } catch (e) {
      console.error("Failed to send installment completion email:", e);
      setMessage(e?.message || "Failed to send email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ padding: 20, maxWidth: 1250 }}>
      <h2 style={{ marginTop: 0 }}>Installment Completion Alerts</h2>
      <p style={{ color: "#555", fontSize: 13, marginTop: -4 }}>
        This page checks main-driver installment plans for the selected statement month. If a plan reaches
        its last installment, it can send one email to Abdulla, Tunzeel, and Saleh so a new contract/price can
        be prepared. Existing cars remain in the system; this is only an alert.
      </p>

      <div
        style={{
          display: "flex",
          gap: 10,
          alignItems: "end",
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 8,
          padding: 12,
          marginBottom: 14,
        }}
      >
        <label style={{ fontSize: 13 }}>
          Statement Month<br />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ padding: 7, minWidth: 150 }}
          />
        </label>
        <button type="button" onClick={loadData} disabled={loading} style={{ padding: "8px 14px" }}>
          {loading ? "Checking..." : "Refresh Check"}
        </button>
        <button
          type="button"
          onClick={sendEmail}
          disabled={sending || !unsentRows.length}
          style={{ padding: "8px 14px", background: unsentRows.length ? "#0f766e" : "#9ca3af", color: "white", border: 0, borderRadius: 4 }}
        >
          {sending ? "Sending..." : `Send Email (${unsentRows.length})`}
        </button>
        <div style={{ fontSize: 12, color: "#555" }}>
          Recipients: {RECIPIENTS.join(", ")}
        </div>
      </div>

      {message && (
        <div style={{ marginBottom: 12, padding: 10, border: "1px solid #cbd5e1", background: "#f8fafc" }}>
          {message}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <Stat label="Configured plans" value={rows.length} />
        <Stat label="Completing/ended this month" value={completingRows.length} />
        <Stat label="Email not sent yet" value={unsentRows.length} />
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ background: "#f3f4f6" }}>
            <tr>
              <th style={th}>Status</th>
              <th style={th}>Car</th>
              <th style={th}>Driver</th>
              <th style={th}>Company</th>
              <th style={th}>Assignment</th>
              <th style={th}>Monthly</th>
              <th style={th}>Installment</th>
              <th style={th}>Base month</th>
              <th style={th}>Email</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 12, textAlign: "center", color: "#666" }}>
                  No configured main-driver installment plans found for {month}.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.assignmentId} style={{ background: r.isCompleting ? "#fff7ed" : "white" }}>
                  <td style={td}>
                    {r.isCompleting ? (r.installmentNo > r.installmentTotal ? "Over total" : "Completing") : "Active"}
                    {r.problem ? <div style={{ color: "#b45309", fontSize: 11 }}>{r.problem}</div> : null}
                  </td>
                  <td style={td}>{r.vehicleNumber}<br /><small>{r.vehicleType}</small></td>
                  <td style={td}>{r.driverName}</td>
                  <td style={td}>{r.company}</td>
                  <td style={td}>{r.startDate} → {r.endDate}</td>
                  <td style={{ ...td, textAlign: "right" }}>AED {money(r.monthlyInstallment)}</td>
                  <td style={td}><b>{r.installmentLabel}</b></td>
                  <td style={td}>{r.installmentBaseMonth}</td>
                  <td style={td}>
                    {r.emailSent ? (
                      <span style={{ color: "#166534" }}>Sent</span>
                    ) : r.isCompleting ? (
                      <span style={{ color: "#b45309" }}>Not sent</span>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", minWidth: 170 }}>
      <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

const th = { textAlign: "left", padding: 8, borderBottom: "1px solid #ddd", whiteSpace: "nowrap" };
const td = { padding: 8, borderBottom: "1px solid #eee", verticalAlign: "top" };
